'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';

const supabase = createClient();

const CATEGORIE = ['hardware','software','rete','sicurezza','procedura','faq','glossario','video','sop','generale'];
const DIFFICOLTA = ['base','intermedio','avanzato'];

const categoriaColor: Record<string, string> = {
  hardware: 'bg-orange-500/20 text-orange-400',
  software: 'bg-blue-500/20 text-blue-400',
  rete: 'bg-sky-500/20 text-sky-400',
  sicurezza: 'bg-red-500/20 text-red-400',
  procedura: 'bg-purple-500/20 text-purple-400',
  faq: 'bg-green-500/20 text-green-400',
  glossario: 'bg-teal-500/20 text-teal-400',
  video: 'bg-pink-500/20 text-pink-400',
  sop: 'bg-amber-500/20 text-amber-400',
  generale: 'bg-gray-500/20 text-gray-400',
};

const difficoltaColor: Record<string, string> = {
  base: 'bg-green-500/20 text-green-400',
  intermedio: 'bg-amber-500/20 text-amber-400',
  avanzato: 'bg-red-500/20 text-red-400',
};

interface Articolo {
  id: string;
  titolo: string;
  sommario: string;
  categoria: string;
  difficolta: string;
  stato: string;
  tipo_dispositivo: string;
  tags: string[];
  autore_nome: string;
  visualizzazioni: number;
  voti_utili: number;
  created_at: string;
}

interface Faq {
  id: string;
  domanda: string;
  risposta: string;
  categoria: string;
  tipo_dispositivo: string;
  voti_utili: number;
  voti_non_utili: number;
}

interface GlossarioItem {
  id: string;
  termine: string;
  definizione: string;
  definizione_semplice: string;
  categoria: string;
  acronimo: string;
}

interface Video {
  id: string;
  titolo: string;
  descrizione: string;
  url_video: string;
  durata_secondi: number;
  categoria: string;
  visualizzazioni: number;
}

interface Sop {
  id: string;
  codice: string;
  titolo: string;
  descrizione: string;
  passi: Array<{ordine: number; titolo: string; descrizione: string}>;
  tipo_dispositivo: string;
  stato: string;
}

interface Quiz {
  id: string;
  titolo: string;
  descrizione: string;
  punteggio_minimo: number;
  tempo_limite_minuti: number;
}

interface Certificazione {
  id: string;
  nome: string;
  ente_certificatore: string;
  descrizione: string;
  tipo_dispositivo: string;
  competenze: string[];
  durata_validita_mesi: number;
}

interface Changelog {
  id: string;
  versione: string;
  titolo: string;
  tipo: string;
  novita: string[];
  correzioni: string[];
  miglioramenti: string[];
  data_rilascio: string;
}

type TabType = 'articoli' | 'sop' | 'faq' | 'video' | 'glossario' | 'quiz' | 'certificazioni' | 'changelog';

export default function KnowledgeBaseContent() {
  const [activeTab, setActiveTab] = useState<TabType>('articoli');
  const [articoli, setArticoli] = useState<Articolo[]>([]);
  const [faq, setFaq] = useState<Faq[]>([]);
  const [glossario, setGlossario] = useState<GlossarioItem[]>([]);
  const [video, setVideo] = useState<Video[]>([]);
  const [sop, setSop] = useState<Sop[]>([]);
  const [quiz, setQuiz] = useState<Quiz[]>([]);
  const [certificazioni, setCertificazioni] = useState<Certificazione[]>([]);
  const [changelog, setChangelog] = useState<Changelog[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [selectedArticolo, setSelectedArticolo] = useState<Articolo | null>(null);
  const [selectedSop, setSelectedSop] = useState<Sop | null>(null);
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);
  const [activeQuiz, setActiveQuiz] = useState<Quiz | null>(null);
  const [quizDomande, setQuizDomande] = useState<any[]>([]);
  const [quizRisposte, setQuizRisposte] = useState<Record<string, number>>({});
  const [quizCompletato, setQuizCompletato] = useState(false);
  const [quizPunteggio, setQuizPunteggio] = useState(0);
  const [showTour, setShowTour] = useState(false);
  const [tourSteps, setTourSteps] = useState<any[]>([]);
  const [tourStep, setTourStep] = useState(0);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [artRes, faqRes, glossRes, vidRes, sopRes, quizRes, certRes, changeRes, tourRes] = await Promise.all([
        supabase.from('kb_articoli').select('*').eq('stato', 'pubblicato').order('visualizzazioni', { ascending: false }),
        supabase.from('kb_faq').select('*').eq('stato', 'pubblicato').order('voti_utili', { ascending: false }),
        supabase.from('kb_glossario').select('*').eq('stato', 'pubblicato').order('termine'),
        supabase.from('kb_video').select('*').eq('stato', 'pubblicato').order('visualizzazioni', { ascending: false }),
        supabase.from('kb_sop').select('*').eq('stato', 'pubblicato').order('codice'),
        supabase.from('kb_quiz').select('*').eq('stato', 'pubblicato'),
        supabase.from('kb_certificazioni').select('*').order('nome'),
        supabase.from('kb_changelog').select('*').order('data_rilascio', { ascending: false }),
        supabase.from('kb_tour_steps').select('*').eq('attivo', true).order('ordine'),
      ]);
      if (artRes.data) setArticoli(artRes.data);
      if (faqRes.data) setFaq(faqRes.data);
      if (glossRes.data) setGlossario(glossRes.data);
      if (vidRes.data) setVideo(vidRes.data);
      if (sopRes.data) setSop(sopRes.data);
      if (quizRes.data) setQuiz(quizRes.data);
      if (certRes.data) setCertificazioni(certRes.data);
      if (changeRes.data) setChangelog(changeRes.data);
      if (tourRes.data) setTourSteps(tourRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleVoto = async (tipo: 'articolo' | 'faq', id: string, utile: boolean) => {
    const table = tipo === 'articolo' ? 'kb_articoli' : 'kb_faq';
    const campo = utile ? 'voti_utili' : 'voti_non_utili';
    const current = tipo === 'articolo'
      ? (articoli.find(a => a.id === id)?.[campo as keyof Articolo] as number || 0)
      : (faq.find(f => f.id === id)?.[campo as keyof Faq] as number || 0);
    await supabase.from(table).update({ [campo]: current + 1 }).eq('id', id);
    fetchData();
  };

  const startQuiz = async (q: Quiz) => {
    const { data } = await supabase.from('kb_quiz_domande').select('*').eq('quiz_id', q.id).order('ordine');
    setQuizDomande(data || []);
    setQuizRisposte({});
    setQuizCompletato(false);
    setQuizPunteggio(0);
    setActiveQuiz(q);
  };

  const submitQuiz = async () => {
    if (!activeQuiz) return;
    let corrette = 0;
    quizDomande.forEach(d => {
      if (quizRisposte[d.id] === d.risposta_corretta) corrette++;
    });
    const punteggio = Math.round((corrette / quizDomande.length) * 100);
    setQuizPunteggio(punteggio);
    setQuizCompletato(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      await supabase.from('kb_quiz_risultati').insert({
        quiz_id: activeQuiz.id,
        utente_id: user.id,
        punteggio,
        risposte: quizRisposte,
        stato: punteggio >= activeQuiz.punteggio_minimo ? 'completato' : 'fallito',
        completato_at: new Date().toISOString(),
      });
    }
  };

  const formatDurata = (sec: number) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const filteredArticoli = articoli.filter(a =>
    (!search || a.titolo.toLowerCase().includes(search.toLowerCase()) || a.sommario?.toLowerCase().includes(search.toLowerCase())) &&
    (!filtroCategoria || a.categoria === filtroCategoria)
  );

  const filteredFaq = faq.filter(f =>
    !search || f.domanda.toLowerCase().includes(search.toLowerCase()) || f.risposta.toLowerCase().includes(search.toLowerCase())
  );

  const filteredGlossario = glossario.filter(g =>
    !search || g.termine.toLowerCase().includes(search.toLowerCase()) || g.definizione.toLowerCase().includes(search.toLowerCase())
  );

  const tabs: { id: TabType; label: string; icon: string; count?: number }[] = [
    { id: 'articoli', label: 'Articoli', icon: 'DocumentTextIcon', count: articoli.length },
    { id: 'sop', label: 'Procedure SOP', icon: 'ClipboardDocumentListIcon', count: sop.length },
    { id: 'faq', label: 'FAQ', icon: 'QuestionMarkCircleIcon', count: faq.length },
    { id: 'video', label: 'Video Tutorial', icon: 'PlayCircleIcon', count: video.length },
    { id: 'glossario', label: 'Glossario', icon: 'BookOpenIcon', count: glossario.length },
    { id: 'quiz', label: 'Quiz', icon: 'AcademicCapIcon', count: quiz.length },
    { id: 'certificazioni', label: 'Certificazioni', icon: 'TrophyIcon', count: certificazioni.length },
    { id: 'changelog', label: 'Changelog', icon: 'ClockIcon', count: changelog.length },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Knowledge Base</h1>
          <p className="text-text-secondary text-sm mt-1">Articoli tecnici, procedure, FAQ, video tutorial e glossario</p>
        </div>
        <button
          onClick={() => setShowTour(true)}
          className="flex items-center gap-2 px-4 py-2 bg-sky-500/10 text-sky-400 border border-sky-500/30 rounded-lg hover:bg-sky-500/20 transition-colors text-sm font-semibold"
        >
          <Icon name="MapIcon" size={16} />
          Tour Guidato
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Icon name="MagnifyingGlassIcon" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
        <input
          type="text"
          placeholder="Cerca in tutta la knowledge base..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-3 bg-surface-elevated border border-border rounded-xl text-white placeholder-text-muted focus:outline-none focus:border-sky-500 text-sm"
        />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto pb-1 scrollbar-thin">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold whitespace-nowrap transition-all ${
              activeTab === tab.id ? 'bg-sky-500/10 text-sky-400 border border-sky-500/30' : 'text-text-secondary hover:text-white hover:bg-surface-elevated'
            }`}
          >
            <Icon name={tab.icon as any} size={16} />
            {tab.label}
            {tab.count !== undefined && (
              <span className="text-xs bg-surface px-1.5 py-0.5 rounded-full">{tab.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* ARTICOLI */}
      {activeTab === 'articoli' && (
        <div className="space-y-4">
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => setFiltroCategoria('')} className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${!filtroCategoria ? 'bg-sky-500/20 text-sky-400' : 'bg-surface-elevated text-text-secondary hover:text-white'}`}>Tutti</button>
            {CATEGORIE.map(c => (
              <button key={c} onClick={() => setFiltroCategoria(c === filtroCategoria ? '' : c)} className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize transition-colors ${filtroCategoria === c ? 'bg-sky-500/20 text-sky-400' : 'bg-surface-elevated text-text-secondary hover:text-white'}`}>{c}</button>
            ))}
          </div>
          {selectedArticolo ? (
            <div className="bg-surface-elevated border border-border rounded-xl p-6 space-y-4">
              <button onClick={() => setSelectedArticolo(null)} className="flex items-center gap-2 text-text-secondary hover:text-white text-sm">
                <Icon name="ArrowLeftIcon" size={16} /> Torna alla lista
              </button>
              <div className="flex items-start justify-between gap-4">
                <h2 className="text-xl font-bold text-white">{selectedArticolo.titolo}</h2>
                <div className="flex gap-2 flex-shrink-0">
                  <span className={`px-2 py-1 rounded-lg text-xs font-semibold capitalize ${categoriaColor[selectedArticolo.categoria]}`}>{selectedArticolo.categoria}</span>
                  <span className={`px-2 py-1 rounded-lg text-xs font-semibold capitalize ${difficoltaColor[selectedArticolo.difficolta]}`}>{selectedArticolo.difficolta}</span>
                </div>
              </div>
              <p className="text-text-secondary text-sm">{selectedArticolo.sommario}</p>
              <div className="flex items-center gap-4 text-xs text-text-muted">
                <span>Autore: {selectedArticolo.autore_nome}</span>
                <span>{selectedArticolo.visualizzazioni} visualizzazioni</span>
                <span>Tags: {selectedArticolo.tags?.join(', ')}</span>
              </div>
              <div className="flex items-center gap-3 pt-2 border-t border-border">
                <span className="text-sm text-text-secondary">Questo articolo è stato utile?</span>
                <button onClick={() => handleVoto('articolo', selectedArticolo.id, true)} className="flex items-center gap-1 px-3 py-1.5 bg-green-500/10 text-green-400 rounded-lg text-sm hover:bg-green-500/20 transition-colors">
                  <Icon name="HandThumbUpIcon" size={14} /> {selectedArticolo.voti_utili}
                </button>
                <button onClick={() => handleVoto('articolo', selectedArticolo.id, false)} className="flex items-center gap-1 px-3 py-1.5 bg-red-500/10 text-red-400 rounded-lg text-sm hover:bg-red-500/20 transition-colors">
                  <Icon name="HandThumbDownIcon" size={14} />
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredArticoli.map(a => (
                <div key={a.id} onClick={() => setSelectedArticolo(a)} className="bg-surface-elevated border border-border rounded-xl p-5 cursor-pointer hover:border-sky-500/50 transition-all group">
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <span className={`px-2 py-1 rounded-lg text-xs font-semibold capitalize ${categoriaColor[a.categoria]}`}>{a.categoria}</span>
                    <span className={`px-2 py-1 rounded-lg text-xs font-semibold capitalize ${difficoltaColor[a.difficolta]}`}>{a.difficolta}</span>
                  </div>
                  <h3 className="font-semibold text-white group-hover:text-sky-400 transition-colors mb-2 line-clamp-2">{a.titolo}</h3>
                  <p className="text-text-secondary text-sm line-clamp-2 mb-3">{a.sommario}</p>
                  <div className="flex items-center justify-between text-xs text-text-muted">
                    <span className="flex items-center gap-1"><Icon name="EyeIcon" size={12} />{a.visualizzazioni}</span>
                    <span className="flex items-center gap-1"><Icon name="HandThumbUpIcon" size={12} />{a.voti_utili}</span>
                    <span>{a.autore_nome}</span>
                  </div>
                </div>
              ))}
              {filteredArticoli.length === 0 && (
                <div className="col-span-3 text-center py-12 text-text-muted">
                  <Icon name="DocumentTextIcon" size={40} className="mx-auto mb-3 opacity-30" />
                  <p>Nessun articolo trovato</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* SOP */}
      {activeTab === 'sop' && (
        <div className="space-y-4">
          {selectedSop ? (
            <div className="bg-surface-elevated border border-border rounded-xl p-6 space-y-4">
              <button onClick={() => setSelectedSop(null)} className="flex items-center gap-2 text-text-secondary hover:text-white text-sm">
                <Icon name="ArrowLeftIcon" size={16} /> Torna alla lista
              </button>
              <div className="flex items-center gap-3">
                <span className="px-2 py-1 bg-amber-500/20 text-amber-400 rounded-lg text-xs font-mono">{selectedSop.codice}</span>
                <h2 className="text-xl font-bold text-white">{selectedSop.titolo}</h2>
              </div>
              <p className="text-text-secondary text-sm">{selectedSop.descrizione}</p>
              <div className="space-y-3">
                <h3 className="font-semibold text-white">Passi della procedura</h3>
                {selectedSop.passi?.map((passo, idx) => (
                  <div key={idx} className="flex gap-4 p-4 bg-surface border border-border rounded-lg">
                    <div className="w-8 h-8 rounded-full bg-sky-500/20 text-sky-400 flex items-center justify-center text-sm font-bold flex-shrink-0">{passo.ordine}</div>
                    <div>
                      <p className="font-semibold text-white text-sm">{passo.titolo}</p>
                      <p className="text-text-secondary text-sm mt-1">{passo.descrizione}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {sop.map(s => (
                <div key={s.id} onClick={() => setSelectedSop(s)} className="bg-surface-elevated border border-border rounded-xl p-5 cursor-pointer hover:border-amber-500/50 transition-all group">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="px-2 py-1 bg-amber-500/20 text-amber-400 rounded-lg text-xs font-mono">{s.codice}</span>
                    {s.tipo_dispositivo && <span className="text-xs text-text-muted">{s.tipo_dispositivo}</span>}
                  </div>
                  <h3 className="font-semibold text-white group-hover:text-amber-400 transition-colors mb-2">{s.titolo}</h3>
                  <p className="text-text-secondary text-sm line-clamp-2 mb-3">{s.descrizione}</p>
                  <div className="flex items-center gap-2 text-xs text-text-muted">
                    <Icon name="ListBulletIcon" size={12} />
                    <span>{s.passi?.length || 0} passi</span>
                  </div>
                </div>
              ))}
              {sop.length === 0 && (
                <div className="col-span-2 text-center py-12 text-text-muted">
                  <Icon name="ClipboardDocumentListIcon" size={40} className="mx-auto mb-3 opacity-30" />
                  <p>Nessuna procedura SOP disponibile</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* FAQ */}
      {activeTab === 'faq' && (
        <div className="space-y-3">
          {filteredFaq.map(f => (
            <details key={f.id} className="bg-surface-elevated border border-border rounded-xl group">
              <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                <div className="flex items-center gap-3">
                  <Icon name="QuestionMarkCircleIcon" size={18} className="text-sky-400 flex-shrink-0" />
                  <span className="font-semibold text-white text-sm">{f.domanda}</span>
                </div>
                <Icon name="ChevronDownIcon" size={16} className="text-text-muted group-open:rotate-180 transition-transform flex-shrink-0" />
              </summary>
              <div className="px-5 pb-5 pt-0 border-t border-border">
                <p className="text-text-secondary text-sm mt-3 leading-relaxed">{f.risposta}</p>
                <div className="flex items-center gap-3 mt-4">
                  <span className="text-xs text-text-muted">Utile?</span>
                  <button onClick={() => handleVoto('faq', f.id, true)} className="flex items-center gap-1 px-2 py-1 bg-green-500/10 text-green-400 rounded text-xs hover:bg-green-500/20 transition-colors">
                    <Icon name="HandThumbUpIcon" size={12} /> {f.voti_utili}
                  </button>
                  <button onClick={() => handleVoto('faq', f.id, false)} className="flex items-center gap-1 px-2 py-1 bg-red-500/10 text-red-400 rounded text-xs hover:bg-red-500/20 transition-colors">
                    <Icon name="HandThumbDownIcon" size={12} /> {f.voti_non_utili}
                  </button>
                </div>
              </div>
            </details>
          ))}
          {filteredFaq.length === 0 && (
            <div className="text-center py-12 text-text-muted">
              <Icon name="QuestionMarkCircleIcon" size={40} className="mx-auto mb-3 opacity-30" />
              <p>Nessuna FAQ trovata</p>
            </div>
          )}
        </div>
      )}

      {/* VIDEO */}
      {activeTab === 'video' && (
        <div className="space-y-4">
          {selectedVideo ? (
            <div className="bg-surface-elevated border border-border rounded-xl p-6 space-y-4">
              <button onClick={() => setSelectedVideo(null)} className="flex items-center gap-2 text-text-secondary hover:text-white text-sm">
                <Icon name="ArrowLeftIcon" size={16} /> Torna alla lista
              </button>
              <h2 className="text-xl font-bold text-white">{selectedVideo.titolo}</h2>
              <div className="aspect-video bg-black rounded-xl overflow-hidden">
                <iframe src={selectedVideo.url_video} className="w-full h-full" allowFullScreen title={selectedVideo.titolo} />
              </div>
              <p className="text-text-secondary text-sm">{selectedVideo.descrizione}</p>
              <div className="flex items-center gap-4 text-xs text-text-muted">
                <span className="flex items-center gap-1"><Icon name="ClockIcon" size={12} />{formatDurata(selectedVideo.durata_secondi)}</span>
                <span className="flex items-center gap-1"><Icon name="EyeIcon" size={12} />{selectedVideo.visualizzazioni} visualizzazioni</span>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {video.map(v => (
                <div key={v.id} onClick={() => setSelectedVideo(v)} className="bg-surface-elevated border border-border rounded-xl overflow-hidden cursor-pointer hover:border-pink-500/50 transition-all group">
                  <div className="aspect-video bg-surface flex items-center justify-center relative">
                    <Icon name="PlayCircleIcon" size={48} className="text-pink-400 group-hover:scale-110 transition-transform" />
                    <span className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-0.5 rounded">{formatDurata(v.durata_secondi)}</span>
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-white group-hover:text-pink-400 transition-colors mb-1 line-clamp-2">{v.titolo}</h3>
                    <p className="text-text-secondary text-xs line-clamp-2">{v.descrizione}</p>
                    <div className="flex items-center gap-2 mt-2 text-xs text-text-muted">
                      <Icon name="EyeIcon" size={12} />{v.visualizzazioni}
                    </div>
                  </div>
                </div>
              ))}
              {video.length === 0 && (
                <div className="col-span-3 text-center py-12 text-text-muted">
                  <Icon name="PlayCircleIcon" size={40} className="mx-auto mb-3 opacity-30" />
                  <p>Nessun video tutorial disponibile</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* GLOSSARIO */}
      {activeTab === 'glossario' && (
        <div className="space-y-3">
          {filteredGlossario.map(g => (
            <div key={g.id} className="bg-surface-elevated border border-border rounded-xl p-5">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-lg font-bold text-sky-400">{g.termine}</span>
                {g.acronimo && g.acronimo !== g.termine && (
                  <span className="px-2 py-0.5 bg-sky-500/10 text-sky-400 rounded text-xs font-mono">{g.acronimo}</span>
                )}
                <span className={`px-2 py-0.5 rounded text-xs font-semibold capitalize ${categoriaColor[g.categoria]}`}>{g.categoria}</span>
              </div>
              <p className="text-white text-sm mb-2">{g.definizione}</p>
              {g.definizione_semplice && (
                <div className="flex items-start gap-2 p-3 bg-surface rounded-lg">
                  <Icon name="LightBulbIcon" size={14} className="text-amber-400 flex-shrink-0 mt-0.5" />
                  <p className="text-text-secondary text-xs italic">{g.definizione_semplice}</p>
                </div>
              )}
            </div>
          ))}
          {filteredGlossario.length === 0 && (
            <div className="text-center py-12 text-text-muted">
              <Icon name="BookOpenIcon" size={40} className="mx-auto mb-3 opacity-30" />
              <p>Nessun termine trovato</p>
            </div>
          )}
        </div>
      )}

      {/* QUIZ */}
      {activeTab === 'quiz' && (
        <div className="space-y-4">
          {activeQuiz ? (
            <div className="bg-surface-elevated border border-border rounded-xl p-6 space-y-6">
              <button onClick={() => setActiveQuiz(null)} className="flex items-center gap-2 text-text-secondary hover:text-white text-sm">
                <Icon name="ArrowLeftIcon" size={16} /> Torna ai quiz
              </button>
              <div>
                <h2 className="text-xl font-bold text-white">{activeQuiz.titolo}</h2>
                <p className="text-text-secondary text-sm mt-1">{activeQuiz.descrizione}</p>
                <div className="flex gap-4 mt-2 text-xs text-text-muted">
                  <span>Punteggio minimo: {activeQuiz.punteggio_minimo}%</span>
                  <span>Tempo limite: {activeQuiz.tempo_limite_minuti} min</span>
                </div>
              </div>
              {quizCompletato ? (
                <div className={`p-6 rounded-xl border text-center ${quizPunteggio >= activeQuiz.punteggio_minimo ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
                  <Icon name={quizPunteggio >= activeQuiz.punteggio_minimo ? 'TrophyIcon' : 'XCircleIcon'} size={48} className={`mx-auto mb-3 ${quizPunteggio >= activeQuiz.punteggio_minimo ? 'text-green-400' : 'text-red-400'}`} />
                  <p className="text-2xl font-bold text-white">{quizPunteggio}%</p>
                  <p className={`text-sm mt-1 ${quizPunteggio >= activeQuiz.punteggio_minimo ? 'text-green-400' : 'text-red-400'}`}>
                    {quizPunteggio >= activeQuiz.punteggio_minimo ? 'Quiz superato!' : 'Quiz non superato. Riprova!'}
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {quizDomande.map((d, idx) => (
                    <div key={d.id} className="space-y-3">
                      <p className="font-semibold text-white text-sm">{idx + 1}. {d.domanda}</p>
                      <div className="space-y-2">
                        {(d.opzioni as string[]).map((opt, optIdx) => (
                          <button
                            key={optIdx}
                            onClick={() => setQuizRisposte(prev => ({ ...prev, [d.id]: optIdx }))}
                            className={`w-full text-left px-4 py-3 rounded-lg border text-sm transition-all ${
                              quizRisposte[d.id] === optIdx
                                ? 'bg-sky-500/20 border-sky-500/50 text-sky-400' :'bg-surface border-border text-text-secondary hover:border-sky-500/30 hover:text-white'
                            }`}
                          >
                            {opt}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                  <button
                    onClick={submitQuiz}
                    disabled={Object.keys(quizRisposte).length < quizDomande.length}
                    className="w-full py-3 bg-sky-500 text-white rounded-lg font-semibold hover:bg-sky-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Invia Risposte ({Object.keys(quizRisposte).length}/{quizDomande.length})
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {quiz.map(q => (
                <div key={q.id} className="bg-surface-elevated border border-border rounded-xl p-5">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <h3 className="font-semibold text-white">{q.titolo}</h3>
                    <Icon name="AcademicCapIcon" size={20} className="text-purple-400 flex-shrink-0" />
                  </div>
                  <p className="text-text-secondary text-sm mb-4">{q.descrizione}</p>
                  <div className="flex items-center gap-4 text-xs text-text-muted mb-4">
                    <span>Min: {q.punteggio_minimo}%</span>
                    <span>Tempo: {q.tempo_limite_minuti} min</span>
                  </div>
                  <button onClick={() => startQuiz(q)} className="w-full py-2 bg-purple-500/10 text-purple-400 border border-purple-500/30 rounded-lg text-sm font-semibold hover:bg-purple-500/20 transition-colors">
                    Inizia Quiz
                  </button>
                </div>
              ))}
              {quiz.length === 0 && (
                <div className="col-span-2 text-center py-12 text-text-muted">
                  <Icon name="AcademicCapIcon" size={40} className="mx-auto mb-3 opacity-30" />
                  <p>Nessun quiz disponibile</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* CERTIFICAZIONI */}
      {activeTab === 'certificazioni' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {certificazioni.map(c => (
            <div key={c.id} className="bg-surface-elevated border border-border rounded-xl p-5">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <h3 className="font-semibold text-white">{c.nome}</h3>
                  <p className="text-text-muted text-xs mt-0.5">{c.ente_certificatore}</p>
                </div>
                <Icon name="TrophyIcon" size={24} className="text-amber-400 flex-shrink-0" />
              </div>
              <p className="text-text-secondary text-sm mb-3">{c.descrizione}</p>
              {c.tipo_dispositivo && (
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="ServerIcon" size={12} className="text-text-muted" />
                  <span className="text-xs text-text-muted">{c.tipo_dispositivo}</span>
                </div>
              )}
              <div className="flex flex-wrap gap-1 mb-3">
                {c.competenze?.map((comp, idx) => (
                  <span key={idx} className="px-2 py-0.5 bg-sky-500/10 text-sky-400 rounded text-xs">{comp}</span>
                ))}
              </div>
              <div className="text-xs text-text-muted">
                Validità: {c.durata_validita_mesi} mesi
              </div>
            </div>
          ))}
          {certificazioni.length === 0 && (
            <div className="col-span-2 text-center py-12 text-text-muted">
              <Icon name="TrophyIcon" size={40} className="mx-auto mb-3 opacity-30" />
              <p>Nessuna certificazione disponibile</p>
            </div>
          )}
        </div>
      )}

      {/* CHANGELOG */}
      {activeTab === 'changelog' && (
        <div className="space-y-4">
          {changelog.map(c => (
            <div key={c.id} className="bg-surface-elevated border border-border rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <span className={`px-3 py-1 rounded-lg text-sm font-bold font-mono ${c.tipo === 'major' ? 'bg-purple-500/20 text-purple-400' : c.tipo === 'minor' ? 'bg-sky-500/20 text-sky-400' : 'bg-gray-500/20 text-gray-400'}`}>
                  v{c.versione}
                </span>
                <div>
                  <h3 className="font-semibold text-white">{c.titolo}</h3>
                  <p className="text-text-muted text-xs">{new Date(c.data_rilascio).toLocaleDateString('it-IT', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {c.novita?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-green-400 mb-2 flex items-center gap-1"><Icon name="PlusCircleIcon" size={12} /> Novità</p>
                    <ul className="space-y-1">
                      {c.novita.map((n, i) => <li key={i} className="text-xs text-text-secondary flex items-start gap-1.5"><span className="text-green-400 mt-0.5">•</span>{n}</li>)}
                    </ul>
                  </div>
                )}
                {c.correzioni?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-red-400 mb-2 flex items-center gap-1"><Icon name="WrenchScrewdriverIcon" size={12} /> Correzioni</p>
                    <ul className="space-y-1">
                      {c.correzioni.map((n, i) => <li key={i} className="text-xs text-text-secondary flex items-start gap-1.5"><span className="text-red-400 mt-0.5">•</span>{n}</li>)}
                    </ul>
                  </div>
                )}
                {c.miglioramenti?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-amber-400 mb-2 flex items-center gap-1"><Icon name="ArrowUpCircleIcon" size={12} /> Miglioramenti</p>
                    <ul className="space-y-1">
                      {c.miglioramenti.map((n, i) => <li key={i} className="text-xs text-text-secondary flex items-start gap-1.5"><span className="text-amber-400 mt-0.5">•</span>{n}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          ))}
          {changelog.length === 0 && (
            <div className="text-center py-12 text-text-muted">
              <Icon name="ClockIcon" size={40} className="mx-auto mb-3 opacity-30" />
              <p>Nessun changelog disponibile</p>
            </div>
          )}
        </div>
      )}

      {/* TOUR GUIDATO MODAL */}
      {showTour && tourSteps.length > 0 && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-surface-elevated border border-border rounded-2xl p-6 max-w-md w-full space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon name="MapIcon" size={20} className="text-sky-400" />
                <span className="font-semibold text-white">Tour Guidato</span>
              </div>
              <span className="text-xs text-text-muted">{tourStep + 1} / {tourSteps.length}</span>
            </div>
            <div className="space-y-2">
              <h3 className="font-bold text-white text-lg">{tourSteps[tourStep]?.titolo}</h3>
              <p className="text-text-secondary text-sm leading-relaxed">{tourSteps[tourStep]?.descrizione}</p>
              <p className="text-xs text-text-muted">Pagina: {tourSteps[tourStep]?.pagina}</p>
            </div>
            <div className="flex items-center gap-2">
              {tourSteps.map((_, i) => (
                <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= tourStep ? 'bg-sky-400' : 'bg-border'}`} />
              ))}
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowTour(false)} className="flex-1 py-2 bg-surface border border-border rounded-lg text-sm text-text-secondary hover:text-white transition-colors">Chiudi</button>
              {tourStep > 0 && (
                <button onClick={() => setTourStep(s => s - 1)} className="px-4 py-2 bg-surface border border-border rounded-lg text-sm text-text-secondary hover:text-white transition-colors">Indietro</button>
              )}
              {tourStep < tourSteps.length - 1 ? (
                <button onClick={() => setTourStep(s => s + 1)} className="flex-1 py-2 bg-sky-500 text-white rounded-lg text-sm font-semibold hover:bg-sky-600 transition-colors">Avanti</button>
              ) : (
                <button onClick={() => setShowTour(false)} className="flex-1 py-2 bg-green-500 text-white rounded-lg text-sm font-semibold hover:bg-green-600 transition-colors">Fine Tour!</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
